import React, { useState, useCallback, useRef } from 'react';
import { cn } from '../lib/utils';

/**
 * HighlightableText - Allows users to highlight text by clicking and dragging
 * Similar to NBME exam interface
 * 
 * Usage: <HighlightableText text="Your vignette text here" className="text-slate-700" />
 */
export function HighlightableText({ text, className, highlightColor = 'bg-yellow-200', showHint = true }) {
    const [highlights, setHighlights] = useState([]);
    const containerRef = useRef(null);

    const handleMouseUp = useCallback(() => {
        const selection = window.getSelection();
        if (!selection || selection.isCollapsed || !containerRef.current) return;

        const range = selection.getRangeAt(0);
        
        // Check if selection is within our container
        if (!containerRef.current.contains(range.commonAncestorContainer)) {
            return;
        }

        const selectedText = selection.toString().trim();
        if (!selectedText) return;

        // Get the start and end positions relative to the full text
        const preSelectionRange = document.createRange();
        preSelectionRange.selectNodeContents(containerRef.current);
        preSelectionRange.setEnd(range.startContainer, range.startOffset);
        const start = preSelectionRange.toString().length;
        const end = start + selectedText.length;

        // Check if this selection overlaps with existing highlights
        const overlappingHighlights = highlights.filter(h => 
            (start < h.end && end > h.start)
        );

        if (overlappingHighlights.length > 0) {
            // Remove overlapping highlights (toggle off)
            setHighlights(prev => prev.filter(h => 
                !(start < h.end && end > h.start)
            ));
        } else {
            // Add new highlight
            setHighlights(prev => [...prev, { start, end, text: selectedText }]);
        }

        // Clear selection
        selection.removeAllRanges();
    }, [highlights]);

    // Render text with highlights
    const renderHighlightedText = () => {
        if (highlights.length === 0) {
            return text;
        }

        // Sort highlights by start position
        const sortedHighlights = [...highlights].sort((a, b) => a.start - b.start);
        
        // Merge overlapping highlights
        const mergedHighlights = [];
        for (const h of sortedHighlights) {
            if (mergedHighlights.length === 0) {
                mergedHighlights.push({ ...h });
            } else {
                const last = mergedHighlights[mergedHighlights.length - 1];
                if (h.start <= last.end) {
                    last.end = Math.max(last.end, h.end);
                } else {
                    mergedHighlights.push({ ...h });
                }
            }
        }

        // Build the rendered text with highlights
        const parts = [];
        let lastIndex = 0;

        for (const h of mergedHighlights) {
            // Add non-highlighted text before this highlight
            if (h.start > lastIndex) {
                parts.push(
                    <span key={`text-${lastIndex}`}>
                        {text.slice(lastIndex, h.start)}
                    </span>
                );
            }
            // Add highlighted text
            parts.push(
                <mark 
                    key={`highlight-${h.start}`} 
                    className={cn(highlightColor, 'rounded px-0.5 transition-colors')}
                >
                    {text.slice(h.start, h.end)}
                </mark>
            );
            lastIndex = h.end;
        }

        // Add remaining text after last highlight
        if (lastIndex < text.length) {
            parts.push(
                <span key={`text-${lastIndex}`}>
                    {text.slice(lastIndex)}
                </span>
            );
        }

        return parts;
    };

    const clearAllHighlights = () => {
        setHighlights([]);
    };

    return (
        <div className="relative">
            <div
                ref={containerRef}
                onMouseUp={handleMouseUp}
                className={cn(
                    "cursor-text select-text leading-relaxed",
                    className
                )}
            >
                {renderHighlightedText()}
            </div>
            <div className="flex items-center justify-between mt-2">
                {showHint && highlights.length === 0 && (
                    <p className="text-xs text-slate-400 italic">
                        Tip: Click and drag to highlight text. Select again to remove.
                    </p>
                )}
                {highlights.length > 0 && (
                    <button
                        onClick={clearAllHighlights}
                        className="text-xs text-slate-400 hover:text-slate-600 underline"
                        title="Clear all highlights"
                    >
                        Clear highlights
                    </button>
                )}
            </div>
        </div>
    );
}

export default HighlightableText;
